import type { Feature, FeatureCollection, MultiPolygon, Point, Polygon } from 'geojson';
import type { DecalSpec, PrismSpec, TotemSpecFile } from './totem-spec';
import {
  validateTotemSpec,
  isBindingImage,
  isColorFill,
  bindingName,
  resolveTotem,
} from './totem-spec';
import { totemTextureImage } from './totem-materials';
import rawSpec from './totems.json';

// Totem geometry interpreter — the "totems on a territory wash" direction
// (docs/game-design/STRUCTURE-GRAPHICS.md). A built structure renders as an
// idealized medieval composition of extruded prisms, DECOUPLED from the real
// building's shape; the host footprint itself stays flat. The compositions
// are DATA (./totems.json, editable via the /editor/ dev page); this module
// only interprets them: spec + anchor → GeoJSON prism features. The mobile
// client interprets the same document with the same rules
// (mobile/lib/features/map/totem_geometry.dart) — keep them in lockstep.

// The canonical compositions, validated once at module init (fail fast on a
// bad checked-in file).
const canonicalSpec: TotemSpecFile = validateTotemSpec(rawSpec);

/** The canonical material list — drives texture loading and the per-material
 *  extrusion layer stack. */
export const canonicalMaterials: string[] = canonicalSpec.materials;

// Extra properties copied onto every produced feature, so a click on any
// prism (or field) can show the structure's details without a second lookup.
export type TotemMeta = Record<string, string | number>;

export interface TotemProperties extends TotemMeta {
  base: number;
  top: number;
  pattern: string;
  color: string;
}

export interface TotemInput {
  type: string;
  geometry: Polygon | MultiPolygon;
  meta?: TotemMeta;
  /** Resolves generic '$name' decal bindings to registered style-image
   *  names. Unresolved bindings render nothing (graceful). */
  bindings?: Record<string, string>;
}

export interface DecalProperties extends TotemMeta {
  /** Resolved style-image name. */
  image: string;
  /** Ground meters the icon spans. */
  size: number;
  rot: number;
}

export interface TotemCollections {
  totems: FeatureCollection<Polygon, TotemProperties>;
  fields: FeatureCollection<Polygon | MultiPolygon, TotemMeta>;
  decals: FeatureCollection<Point, DecalProperties>;
}

type Ring = number[][];
type PrismFeature = Feature<Polygon, TotemProperties>;

// buildTotemCollections interprets the canonical spec for a set of built
// structures. Unknown types produce nothing (a future structure type renders
// as a bare host until its composition ships).
export function buildTotemCollections(built: TotemInput[]): TotemCollections {
  return buildTotemCollectionsFromSpec(canonicalSpec, built);
}

// buildTotemCollectionsFromSpec is the spec-parameterized interpreter — the
// editor previews in-progress documents through this without touching the
// canonical file.
export function buildTotemCollectionsFromSpec(
  spec: TotemSpecFile,
  built: TotemInput[],
): TotemCollections {
  const totems: PrismFeature[] = [];
  const fields: Feature<Polygon | MultiPolygon, TotemMeta>[] = [];
  const decals: Feature<Point, DecalProperties>[] = [];
  for (const b of built) {
    const totemName = resolveTotem(spec, b.type);
    const typeSpec = totemName === null ? undefined : spec.types[totemName];
    if (!typeSpec) continue;
    const meta = b.meta ?? {};
    if (typeSpec.footprintField !== undefined) {
      // pattern routes the feature to its material's fill layer; '' routes
      // it to the plain-color fields layer (mirrors the prism convention).
      const color = isColorFill(typeSpec.footprintField);
      fields.push({
        type: 'Feature',
        geometry: b.geometry,
        properties: {
          ...meta,
          pattern: color ? '' : typeSpec.footprintField,
          color: color ? typeSpec.footprintField : '#888888',
        },
      });
    }
    const c = anchor(b.geometry);
    const m = meterScaleAt(c[1]);
    for (const prism of typeSpec.prisms) {
      totems.push(interpretPrism(prism, c, m, meta));
    }
    for (const decal of typeSpec.decals ?? []) {
      const feature = interpretDecal(decal, c, m, meta, b.bindings);
      if (feature) decals.push(feature);
    }
  }
  return {
    totems: { type: 'FeatureCollection', features: totems },
    fields: { type: 'FeatureCollection', features: fields },
    decals: { type: 'FeatureCollection', features: decals },
  };
}

function interpretDecal(
  d: DecalSpec,
  anchorPt: number[],
  m: MeterScale,
  meta: TotemMeta,
  bindings: Record<string, string> | undefined,
): Feature<Point, DecalProperties> | null {
  // Materials resolve to their registered tile; '$name' bindings resolve via
  // the caller's map, and unresolved slots are simply omitted.
  const image = isBindingImage(d.image)
    ? bindings?.[bindingName(d.image)]
    : totemTextureImage(d.image);
  if (!image) return null;
  return {
    type: 'Feature',
    geometry: {
      type: 'Point',
      coordinates: [
        anchorPt[0] + (d.dx ?? 0) * m.lngPerMeter,
        anchorPt[1] + (d.dy ?? 0) * m.latPerMeter,
      ],
    },
    properties: { ...meta, image, size: d.size, rot: d.rot ?? 0 },
  };
}

// ── prism interpretation ────────────────────────────────────────────────────

function interpretPrism(
  p: PrismSpec,
  anchorPt: number[],
  m: MeterScale,
  meta: TotemMeta,
): PrismFeature {
  const center = [
    anchorPt[0] + (p.dx ?? 0) * m.lngPerMeter,
    anchorPt[1] + (p.dy ?? 0) * m.latPerMeter,
  ];
  const rotRad = ((p.rot ?? 0) * Math.PI) / 180;

  // Ring corners in meter space around the prism's own center, rotated,
  // then converted per-axis to degrees. rot 0 reproduces the pre-data-driven
  // hand-coded coordinates exactly.
  let metersRing: number[][];
  if (p.shape === 'rect') {
    const hw = p.w / 2;
    const hh = p.h / 2;
    metersRing = [
      [-hw, -hh],
      [hw, -hh],
      [hw, hh],
      [-hw, hh],
      [-hw, -hh],
    ];
  } else {
    metersRing = [];
    for (let i = 0; i <= p.sides; i++) {
      const a = (2 * Math.PI * i) / p.sides;
      metersRing.push([p.r * Math.cos(a), p.r * Math.sin(a)]);
    }
  }

  const cos = Math.cos(rotRad);
  const sin = Math.sin(rotRad);
  const ring: Ring = metersRing.map(([x, y]) => [
    center[0] + (x * cos - y * sin) * m.lngPerMeter,
    center[1] + (x * sin + y * cos) * m.latPerMeter,
  ]);

  return {
    type: 'Feature',
    geometry: { type: 'Polygon', coordinates: [ring] },
    properties: {
      ...meta,
      base: p.base ?? 0,
      top: p.top,
      pattern: p.pattern ?? '',
      color: p.color ?? '#888888',
    },
  };
}

// ── geometry helpers ────────────────────────────────────────────────────────

function outerRings(geometry: Polygon | MultiPolygon): Ring[] {
  const polys = geometry.type === 'Polygon' ? [geometry.coordinates] : geometry.coordinates;
  return polys.map((poly) => poly[0].map((p) => [p[0], p[1]]));
}

// Totem anchor: centroid of the largest outer ring (multipolygon hosts get
// one totem, on their biggest part).
function anchor(geometry: Polygon | MultiPolygon): number[] {
  const rings = outerRings(geometry);
  let best = rings[0];
  let bestSpan = ringSpan(best);
  for (const r of rings.slice(1)) {
    const s = ringSpan(r);
    if (s > bestSpan) {
      best = r;
      bestSpan = s;
    }
  }
  return ringCentroid(best);
}

function ringCentroid(ring: Ring): number[] {
  let x = 0;
  let y = 0;
  for (const p of ring) {
    x += p[0];
    y += p[1];
  }
  return [x / ring.length, y / ring.length];
}

function ringSpan(ring: Ring): number {
  let minX = Infinity;
  let maxX = -Infinity;
  let minY = Infinity;
  let maxY = -Infinity;
  for (const p of ring) {
    minX = Math.min(minX, p[0]);
    maxX = Math.max(maxX, p[0]);
    minY = Math.min(minY, p[1]);
    maxY = Math.max(maxY, p[1]);
  }
  return (maxX - minX) * (maxY - minY);
}

interface MeterScale {
  latPerMeter: number;
  lngPerMeter: number;
}

// Meters→degrees at a latitude, so totems keep true size everywhere.
function meterScaleAt(lat: number): MeterScale {
  return {
    latPerMeter: 1 / 111320.0,
    lngPerMeter: 1 / (111320.0 * Math.cos((lat * Math.PI) / 180)),
  };
}
